import React, { useState } from 'react';
import { Box, Typography, Container, TextField, Button, Grid, Paper, IconButton, useTheme } from '@mui/material';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import { gradients } from '../theme';
import FadeIn from './FadeIn';

const Contact: React.FC = () => {
    const theme = useTheme();
    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        email: '',
        notes: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        console.log('Form submitted:', formData);
    };

    // Compact TextField Style
    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            color: 'white',
            borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            transition: 'all 0.3s ease',
            fontSize: '0.9rem',
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
            '&.Mui-focused': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                '& fieldset': { borderColor: '#00f2ff' },
            },
        },
        '& .MuiInputLabel-root': {
            color: 'grey.500',
            fontSize: '0.9rem',
            '&.Mui-focused': { color: '#00f2ff' },
        },
    };

    const contactDetails = [
        {
            icon: <MapPin size={24} />,
            title: 'Visit Us',
            text: 'Warehouse No.5, Industrial area 12, Beside GECO Building, Sharjah - UAE',
        },
        {
            icon: <Phone size={24} />,
            title: 'Call Us',
            text: 'Mob: 054 461 1878 | Tel: 06 715 1943',
        },
        {
            icon: <Mail size={24} />,
            title: 'Email Us',
            text: 'info@bluecatrecycling.com',
            action: true,
        },
    ];

    return (
        <Box
            id="contact"
            sx={{
                backgroundColor: 'black',
                py: 10,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Background Glow */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: -100,
                    left: -100,
                    width: 600,
                    height: 600,
                    background: 'radial-gradient(circle, rgba(0, 242, 255, 0.05) 0%, transparent 60%)',
                    borderRadius: '50%',
                    filter: 'blur(100px)',
                    zIndex: 0,
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={8} alignItems="center">

                    {/* Left: Contact Info */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <FadeIn direction="right">
                            <Box sx={{ pr: { md: 4 } }}>
                                <Typography
                                    variant="overline"
                                    sx={{ color: '#00f2ff', fontWeight: 600, letterSpacing: 1.5 }}
                                >
                                    CONTACT US
                                </Typography>
                                <Typography
                                    variant="h2"
                                    sx={{
                                        ...gradients.primaryText,
                                        mb: 3,
                                        fontWeight: 700,
                                        letterSpacing: '-1px',
                                        lineHeight: 1.1,
                                    }}
                                >
                                    Get in Touch
                                </Typography>
                                <Typography sx={{ color: 'grey.500', mb: 6, fontSize: '1rem', lineHeight: 1.6 }}>
                                    Have questions about catalytic converter recycling? Reach out to our team for expert assistance and live quotes.
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {contactDetails.map((item, index) => (
                                        <Box key={index} sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                                            <IconButton
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    backgroundColor: 'rgba(255,255,255,0.03)',
                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                    color: '#00f2ff',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0,242,255,0.1)',
                                                        borderColor: '#00f2ff'
                                                    },
                                                }}
                                            >
                                                {item.icon}
                                            </IconButton>
                                            <Box>
                                                <Typography variant="body1" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                                                    {item.title}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'grey.400', fontSize: '0.9rem' }}>
                                                    {item.text}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </FadeIn>
                    </Grid>

                    {/* Right: Modern Compact Form */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <FadeIn direction="left" delay={0.2}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 4, md: 5 },
                                    backgroundColor: 'rgba(20, 20, 22, 0.6)',
                                    backdropFilter: 'blur(20px)',
                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                    borderRadius: 4,
                                    boxShadow: '0 20px 80px -20px rgba(0,0,0,0.5)',
                                }}
                            >
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth name="name" label="Name"
                                                value={formData.name} onChange={handleChange} variant="outlined" sx={textFieldSx} size="small"
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                fullWidth name="mobile" label="Phone"
                                                value={formData.mobile} onChange={handleChange} variant="outlined" sx={textFieldSx} size="small"
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                fullWidth name="email" label="Email" type="email"
                                                value={formData.email} onChange={handleChange} variant="outlined" sx={textFieldSx} size="small"
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <TextField
                                                fullWidth name="notes" label="Message" multiline rows={4}
                                                value={formData.notes} onChange={handleChange} variant="outlined" sx={textFieldSx}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Button
                                                fullWidth
                                                type="submit"
                                                variant="contained"
                                                endIcon={<Send size={18} />}
                                                sx={{
                                                    background: gradients.primary,
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    py: 1.8,
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    fontSize: '1rem',
                                                    boxShadow: '0 4px 20px rgba(0, 242, 255, 0.2)',
                                                    '&:hover': {
                                                        boxShadow: '0 4px 30px rgba(0, 242, 255, 0.4)',
                                                    }
                                                }}
                                            >
                                                Send Message
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Paper>
                        </FadeIn>
                    </Grid>

                </Grid>
            </Container>
        </Box>
    );
};

export default Contact;
